import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSetAtom } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import useSharedScreen, { viewPasswordAtom } from "./useSharedView";
import SharedViewAPI from "../../api/api/SharedScreenAPI";
import PageViewRenderer from "../../components/PageView/PageViewRenderer";
import { setSchemaAtom } from "../../atoms/GlobalAtoms";
import { newToastEvent } from "../../utils/ToastUtil";
import LoadingView from "./LoadingView";
import PasswordProtectedView from "./PasswordProtectedView";
import NotFoundView from "./NotFoundView";

export default function SharedViewLayout() {
  const setViewPasswords = useSetAtom(viewPasswordAtom);
  const setSchema = useSetAtom(setSchemaAtom);
  const client = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const { tenantID, sharedViewID, recordID }: any = useParams();
  const { sharedView, sharedViewLoading } = useSharedScreen(
    tenantID,
    sharedViewID,
    recordID
  );

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (err) => {
      if (
        err?.config?.url?.includes("/getSharedView/") &&
        err?.response?.status === 400 &&
        err?.response?.data === "Bad Request"
      ) {
        navigate(`/login?r=${window.location.href}`);
        newToastEvent("error", "Please login to access that page.");
      }

      return Promise.reject(err);
    }
  );

  const success = sharedView?.success;
  const requiresPassword = sharedView?.requiresPassword;

  if (sharedViewLoading) return <LoadingView />;

  if (!success && requiresPassword) {
    return (
      <PasswordProtectedView
        onSubmit={updatePassword}
        submitting={submitting}
        error={error}
      />
    );
  }

  if (success) {
    return (
      <PageViewRenderer
        page={{
          Name: sharedView?.view?.Name,
          Views: [sharedView.view],
          Description: "",
          IconURL: "",
          Privacy: "public",
          Owner: "",
          Icon: "",
          Members: [],
          _id: "",
          id: "",
          Created: "",
          CreatedBy: "",
          LastUpdated: "",
          LastUpdatedBy: "",
          Tags: [],
        }}
        pageLoading={false}
        view={sharedView.view}
        viewLoading={false}
        viewID={sharedView.view._id}
        record={sharedView.record}
        showInsights={true}
        commentsWidget={false}
      />
    );
  }

  // Unexpected error message here
  return <NotFoundView />;

  async function updatePassword(password: string) {
    if (!password) return;

    setError("");
    setSubmitting(true);

    setViewPasswords((prev) => ({
      ...prev,
      [`${tenantID}-${sharedViewID}${recordID ? `-${recordID}` : ""}`]:
        password,
    }));

    try {
      const result = await SharedViewAPI.getSharedView(
        tenantID,
        sharedViewID,
        recordID,
        password
      );

      const data = result.data.data;

      if (data.success === false) {
        setError("Invalid password provided.");
      } else if (data.success === true) {
        setSchema(data.schema);

        client.setQueryData(["shared-view", sharedViewID, recordID], data);

        for (const record of data?.documentRecords || []) {
          client.setQueryData(
            [tenantID, "record", "Documents", record._id],
            record
          );
        }

        for (const flow of data?.flowCharts || []) {
          client.setQueryData([tenantID, "record", "Flows", flow._id], flow);
        }
      }
    } catch {
      setError("An unexpected error");
    } finally {
      setSubmitting(false);
    }
  }
}
